import { useState } from 'react';
import { UpdateOrderStatus } from '../../types/opsBriefing';
import { OrderView, toOrderViews } from './adapters';
import { useOpsContext } from './useOpsContext';
import {
    useOpsOrdersQuery,
    useOpsRetractedOrdersQuery,
    useUpdateOpsOrderMutation,
} from '../../services/useOpsBriefingService';
import { OpsLoading, OpsError } from './OpsStates';
import { useOrderRetract } from './OrderRetractModal';
import { describeRetractDelivery } from './retractDelivery';

/**
 * 지시 및 진행 현황 — 체크리스트.
 *
 * 이전에는 상태 뱃지(전달 완료·대기중 / 강사 확인함 / 미실행)만 나열했다.
 * 뱃지는 읽어야 뜻을 알 수 있어서 "그래서 이건 된 거야 안 된 거야"가 한 번에 안 잡혔다.
 *
 * 그래서 체크박스로 바꾼다. 네모가 비어 있으면 아직, 초록 체크면 끝난 것.
 * 그리고 안 된 것을 위로, 끝난 것을 아래로 내려서 위에서부터 처리하면 되게 만든다.
 *
 * ─────────────────────────────────────────────────────────────
 * 데이터는 전부 서버에서 옵니다
 * ─────────────────────────────────────────────────────────────
 * 예전에는 `useState(mockOrders)` 로 목을 초기값으로 썼습니다. 그러면
 * 게이트가 잠긴 빈 화면에도 없는 회원 이름이 뜨고, 체크한 결과는
 * 새로고침하면 사라집니다. 원장님이 "강사에게 전달됐다"고 믿은 것이
 * 아무 데도 안 남습니다.
 *
 * 조회는 useOpsOrdersQuery, 변경은 useUpdateOpsOrderMutation 입니다.
 * 낙관적 업데이트는 훅 안에 있습니다 — 실패하면 이전 목록으로 되돌아옵니다.
 *
 * ─────────────────────────────────────────────────────────────
 * 철회한 지시는 왜 따로 부르는가
 * ─────────────────────────────────────────────────────────────
 * 서버 기본 목록은 정리된 건을 전부 뺍니다(ARCHIVED · RETRACTED). 그대로 두면
 * 원장이 "내가 뭘 취소했더라"를 확인할 통로가 없고, 무엇보다 **철회가 강사에게
 * 전달되지 못한 건(FAILED)** 을 알 수가 없습니다. 그건 강사가 취소된 지시를
 * 그대로 실행하고 있다는 뜻이라 원장이 직접 연락해야 하는 상태입니다.
 *
 * 그래서 includeArchived=true 로 한 번 더 부릅니다. 진행 중 목록까지 그 옵션으로
 * 대신 부르지 않는 이유는 서버 상한(sentAt 내림차순 200건) 때문입니다 —
 * 몇 달치 정리된 건이 그 창을 채우면 아직 처리 안 된 오래된 지시가 밀려납니다.
 */

const BORDER = '#E3EAF3';

/** 안 된 것 / 된 것, 딱 두 갈래로만 나눈다 */
const isDone = (o: OrderView) => o.status === 'DONE';

/** 아직 안 된 건들의 진행 단계 — 뱃지 대신 짧은 한 줄로 */
const pendingNote = (o: OrderView) => {
    if (o.reopened)
        return {
            text: '강사는 완료했다고 보고했지만 원장님이 다시 열었습니다',
            tone: '#B45309',
        };
    /*
     * "24시간 넘게 실행되지 않았습니다" 였다. 그건 강사를 탓하는 문장이다.
     * 푸시가 나가게 된 지금도(2026-08-26~) 그대로 두지 않는다 — 알림을 껐거나
     * 기기가 등록되지 않은 강사에게는 여전히 닿지 않고, 웹은 닿았는지 모른다.
     * 강사가 열어봤는지는 SEEN 이 말해 준다. 그 전까지는 시간만 말한다.
     */
    if (o.status === 'OVERDUE') return { text: '지시한 지 24시간이 지났습니다', tone: '#C22D38' };
    if (o.status === 'SEEN')
        return {
            // 확인 시각을 모르면 시각을 지어내지 않는다
            text: o.seenLabel
                ? `강사가 ${o.seenLabel}에 확인했습니다 · 실행 대기중`
                : '강사가 확인했습니다 · 실행 대기중',
            tone: '#B45309',
        };
    /*
     * 한때 "전달 · 아직 확인 전입니다"였다가, 실제로는 알림이 안 나가던 시절에
     * "기록 · 강사에게 직접 전해 주세요"로 내려 적었다.
     * 2026-08-26 부터는 푸시가 실제로 나간다(OPS_ORDER_PUSH_ENABLED=true).
     *
     * 그래도 "강사가 받았습니다"라고는 쓰지 않는다. 알림을 껐거나 기기가 없으면
     * 안 가고, 웹은 그 결과를 알 방법이 없다(OrderConfirmModal COACH_PUSH_NOTICE 주석).
     * 우리가 아는 사실은 **보냈고, 아직 강사가 열어보지 않았다**까지다.
     */
    return { text: `${o.sentLabel} 발송 · 강사가 아직 열어보지 않았습니다`, tone: '#7C8794' };
};

/** 그룹 구분선 — 세 덩어리가 같은 문법으로 읽혀야 한다 */
const GroupHeader = ({
    title,
    hint,
    color,
    hintColor,
    bg,
    topBorder,
}: {
    title: string;
    hint: string;
    color: string;
    hintColor: string;
    bg: string;
    topBorder?: boolean;
}) => (
    <div
        className="flex items-center"
        style={{
            padding: '11px 22px',
            background: bg,
            gap: 8,
            borderTop: topBorder ? `1px solid ${BORDER}` : undefined,
        }}
    >
        <span style={{ fontSize: 14, fontWeight: 800, color }}>{title}</span>
        <span style={{ fontSize: 13, color: hintColor }}>{hint}</span>
    </div>
);

const OrderTracker = () => {
    const ctx = useOpsContext();
    const { data, isLoading, isError, refetch } = useOpsOrdersQuery(ctx, ctx.ready);
    /*
     * 철회한 건은 기본 목록에서 빠지므로 따로 부릅니다.
     * 이 조회가 실패해도 진행 현황은 그대로 보여줍니다 — 오늘 할 일을 못 보게
     * 만드는 것이 더 나쁩니다. 대신 아래에서 "못 불러왔다"고 말해 줍니다.
     */
    const retractedQuery = useOpsRetractedOrdersQuery(ctx, ctx.ready);
    const updateOrder = useUpdateOpsOrderMutation(ctx);
    const [toast, setToast] = useState<string | null>(null);
    /** 철회 기록은 매일 보는 것이 아니라 접어 둔다 (전달 실패 건은 아래에서 따로 꺼낸다) */
    const [showRetracted, setShowRetracted] = useState(false);

    const fire = (msg: string) => {
        setToast(msg);
        window.setTimeout(() => setToast(null), 2400);
    };

    const retract = useOrderRetract(ctx, { notify: fire });

    const orders = toOrderViews(data ?? []);

    /*
     * 상태 변경은 전부 이 한 곳을 지납니다.
     * 실패하면 훅이 목록을 되돌리므로, 화면에는 실패했다고 말해 줘야 합니다 —
     * 체크가 조용히 풀리면 원장님은 자기가 잘못 눌렀다고 생각합니다.
     */
    const changeStatus = (orderId: string, status: UpdateOrderStatus, okMessage: string) => {
        updateOrder.mutate(
            { orderId, body: { status } },
            {
                onSuccess: () => fire(okMessage),
                onError: () => fire('처리하지 못했습니다. 잠시 후 다시 시도해 주세요'),
            }
        );
    };

    /*
     * 진행 현황에 세는 건.
     *
     * 철회한 건은 여기서 빠집니다 — 분모에도 분자에도 들어가지 않습니다.
     * 서버도 어제 지시 요약에서 같은 이유로 양쪽에서 뺍니다. 분모에 남기면
     * **원장의 실수가 강사의 완료율을 깎습니다.** 하지 말라고 해 놓고
     * "안 했습니다"라고 셀 수는 없습니다.
     *
     * 서버 기본 목록이 이미 빼고 주지만 한 번 더 거릅니다. 목 모드와 캐시에
     * 남은 이전 응답이 섞일 수 있고, 이 한 줄이 없으면 방금 철회한 건이
     * 낙관 반영 없이도 잠깐 '미완료'로 되살아납니다.
     */
    const visible = orders.filter((o) => o.status !== 'ARCHIVED' && o.status !== 'RETRACTED');
    const todo = visible.filter((o) => !isDone(o));
    // 오늘 낸 지시는 아직 기다려도 되는 것이고, 어제 이전 건은 밀린 것이다.
    // 같은 '미완료'라도 원장이 취해야 할 행동이 다르다.
    const todayTodo = todo.filter((o) => o.sentDayOffset === 0);
    const backlog = todo.filter((o) => o.sentDayOffset > 0);
    const done = visible.filter(isDone);
    const overdue = todo.filter((o) => o.status === 'OVERDUE').length;

    /*
     * 금액을 산정할 수 없는 건이 섞입니다(단가 없는 수강권).
     * 합계에 0 으로 더하면 "이만큼 지켰습니다"가 조용히 낮아지고,
     * 몇 건이 빠졌는지 알 수 없습니다. 빼고 세되 건수를 따로 밝힙니다.
     */
    const sumValue = (list: OrderView[]) => list.reduce((s, o) => s + (o.valueAmount ?? 0), 0);
    const unpriced = visible.filter((o) => o.valueAmount == null).length;
    // 이름을 뜻에 맞춘다 — 지킨 돈이 아니라 그 건에 걸려 있던 추정 금액이다
    const doneValue = sumValue(done);
    const todoValue = sumValue(todo);
    const donePct = visible.length ? Math.round((done.length / visible.length) * 100) : 0;

    /*
     * 철회한 지시 — 방금 취소된 것이 위로 온다.
     *
     * sentAt(=sentLabel) 으로 정렬하면 안 됩니다. 오래된 지시를 오늘 철회하면
     * 맨 아래로 가서, 가장 봐야 할 것이 가장 안 보이는 자리로 갑니다.
     */
    const retractedAll = toOrderViews(retractedQuery.data ?? [])
        .filter((o) => o.status === 'RETRACTED')
        .sort((a, b) => (b.retractedAt ?? '').localeCompare(a.retractedAt ?? ''));
    /*
     * 전달 실패는 접힌 서랍에 넣지 않습니다.
     *
     * 강사가 이 지시를 취소된 줄 모른다는 뜻이라, 원장이 **직접 연락해야**
     * 합니다. 나머지 철회 기록과 같은 자리에 두면 아무도 안 펴 봅니다.
     * 대신 한 건이 두 곳에 나오지는 않게 아래 서랍에서는 뺍니다.
     */
    const undelivered = retractedAll.filter(
        (o) => describeRetractDelivery(o)?.needsAction === true
    );
    const retractedRest = retractedAll.filter((o) => !undelivered.includes(o));
    /** 아직 보내는 중인 건 — 서랍을 안 펴도 몇 건인지는 보이게 한다 */
    const stillSending = retractedRest.filter((o) => o.retractDelivery === 'PENDING').length;

    /**
     * 원장이 직접 체크.
     * 대면으로 이미 이야기가 끝난 건이 있어서, 강사 보고만 기다리면 목록이 안 줄어든다.
     * 다만 누가 완료 처리했는지는 서버가 남긴다(doneBy: 'OWNER') — 강사 보고와 구분된다.
     */
    const markDone = (orderId: string) => changeStatus(orderId, 'DONE', '완료 처리했습니다');

    /**
     * 완료 해제.
     * 잘못 눌렀을 수도 있고, 강사가 됐다고 했는데 실제로는 아닐 수도 있다.
     * 후자가 더 중요한 경우라서, 서버가 강사 보고는 지우지 않고 '다시 열림'으로 남긴다.
     *
     * 되돌릴 상태는 기록으로 되짚는다 — 확인했으면 SEEN, 아니면 SENT.
     * 어제 이전 건인데 확인조차 없었다면 원래 기한 초과였다.
     * (서버는 OVERDUE 를 저장하지 않고 SENT 로 두었다가 조회 시 다시 계산한다)
     */
    const undoDone = (order: OrderView) => {
        const back: UpdateOrderStatus = order.seenAt
            ? 'SEEN'
            : order.sentDayOffset > 0
              ? 'OVERDUE'
              : 'SENT';
        changeStatus(order.orderId, back, '완료를 해제했습니다');
    };

    const archive = (orderId: string) =>
        changeStatus(orderId, 'ARCHIVED', '확인 처리했습니다. 목록에서 정리됩니다');

    /*
     * 강사에게 전달되지 못한 철회 — 맨 위.
     *
     * 이 화면에서 원장이 **직접 움직여야 하는** 유일한 상태다. 밀린 지시보다
     * 위에 두는 것은, 밀린 지시는 아직 시간이 있지만 이건 잘못된 일이 이미
     * 진행 중일 수 있기 때문이다.
     *
     * 변수로 빼 둔 이유가 있다. **진행 목록 조회가 실패한 화면에서도 이건
     * 보여야 한다.** 예전에는 에러 화면이 통째로 조기 반환이라, 지시 목록이
     * 한 번 실패하면 "강사에게 전달 못 한 철회가 있다"는 경고까지 같이
     * 사라졌다. 서로 다른 두 조회인데 한쪽 실패가 다른 쪽 사실을 덮었다.
     */
    const UndeliveredBand =
        undelivered.length > 0 ? (
            <>
                <GroupHeader
                    title={`강사에게 전달되지 못한 철회 ${undelivered.length}건`}
                    hint="철회 알림이 닿지 않았습니다 · 직접 연락해 주세요"
                    color="#C22D38"
                    hintColor="#A7706F"
                    bg="#FFF1F1"
                />
                {undelivered.map((o) => (
                    <RetractedRow key={o.orderId} order={o} />
                ))}
            </>
        ) : null;

    const Header = (
        <div className="mb-[12px] flex items-baseline justify-between">
            <div className="flex items-baseline" style={{ gap: 10 }}>
                <span style={{ fontSize: 18, fontWeight: 700, color: '#16202E' }}>
                    지시 및 진행 현황
                </span>
                <span style={{ fontSize: 13.5, color: '#93A0AE' }}>
                    지시한 내용이 실제로 실행됐는지 추적합니다
                </span>
            </div>
            {overdue > 0 && (
                <span
                    className="rounded-[7px]"
                    style={{
                        background: '#FFE8E9',
                        color: '#C22D38',
                        fontSize: 13,
                        fontWeight: 700,
                        padding: '4px 10px',
                    }}
                >
                    기한 초과 {overdue}건
                </span>
            )}
        </div>
    );

    /* 진행 목록이 없어도 전달 실패한 철회는 보여야 한다 — 별개의 조회다 */
    const bandOnly = UndeliveredBand && (
        <div
            className="w-full rounded-[14px] border border-solid bg-white"
            style={{ borderColor: BORDER, overflow: 'hidden', marginBottom: 12 }}
        >
            {UndeliveredBand}
        </div>
    );

    if (isLoading) {
        return (
            <div>
                {Header}
                {bandOnly}
                <OpsLoading variant="panel" />
            </div>
        );
    }

    if (isError) {
        return (
            <div>
                {Header}
                {bandOnly}
                <OpsError title="지시 목록을 불러오지 못했습니다" onRetry={() => refetch()} />
            </div>
        );
    }

    return (
        <div>
            {Header}

            <div
                className="w-full rounded-[14px] border border-solid bg-white"
                style={{ borderColor: BORDER, overflow: 'hidden' }}
            >
                {/*
                  진행률 — 숫자보다 막대가 먼저 읽힌다.

                  ⚠️ 지시가 하나도 없으면 진행률 블록 자체를 그리지 않는다.
                  예전에는 "0 / 0건 완료" 와 0% 짜리 회색 막대가 그대로 떴다.
                  0/0 은 아무 뜻도 없는 숫자인데, 빈 막대는 "해야 할 일을 하나도
                  못 했다"처럼 읽힌다 — 실제로는 시킬 일이 없었던 날이다.
                  아래 안내문만 남긴다.
                */}
                {visible.length > 0 && (
                <div style={{ padding: '16px 22px 14px', borderBottom: `1px solid ${BORDER}` }}>
                    <div className="flex items-baseline justify-between">
                        <div className="flex items-baseline" style={{ gap: 8 }}>
                            <span style={{ fontSize: 22, fontWeight: 800, color: '#16202E', letterSpacing: '-0.02em' }}>
                                {done.length}
                            </span>
                            <span style={{ fontSize: 15, color: '#7C8794' }}>
                                / {visible.length}건 완료
                            </span>
                        </div>
                        <div className="flex items-center" style={{ gap: 14 }}>
                            {/*
                              * ⚠️ 이 두 숫자는 **걸린 금액**이지 지킨 금액이 아니다.
                              *
                              * valueAmount 는 지시를 낼 때 박은 사전 추정액이고(잔여 회차 × 단가 +
                              * 다음 수강권 예상액 × 재등록률), 완료는 **강사가 완료 버튼을 눌렀다**는
                              * 뜻이다. 그 회원이 실제로 돌아왔는지는 아직 아무도 재지 않았다.
                              *
                              * 예전 문구는 "지켜낸 매출"이었다. 재지 않은 것을 쟀다고 말하는 문구다.
                              * 이 화면의 다른 자리(지난 리포트 effect/defended)는 같은 이유로 null 을
                              * 내려보내며 "그 추적이 아직 없으므로"라고 서버가 직접 적어 두었다.
                              * 한 화면 안에서 한쪽만 단정하면 안 된다.
                              *
                              * 실측(OpsBriefingPhase2.md 의 outcome60d)이 들어오면 그때
                              * "지시 후 재등록 결제"를 실제 결제액으로 따로 표시한다.
                              */}
                            {todoValue > 0 && (
                                <span style={{ fontSize: 13.5, color: '#C22D38' }}>
                                    아직 못 챙긴 건의 대상 금액 <b>{todoValue.toLocaleString('ko-KR')}원</b>
                                </span>
                            )}
                            {doneValue > 0 && (
                                <span style={{ fontSize: 13.5, color: '#00795F' }}>
                                    완료된 건의 대상 금액 <b>{doneValue.toLocaleString('ko-KR')}원</b>
                                </span>
                            )}
                            {/* 합계에서 빠진 건수를 밝힌다. 안 밝히면 합계를 전부로 읽는다 */}
                            {unpriced > 0 && (
                                <span style={{ fontSize: 12.5, color: '#93A0AE' }}>
                                    금액 산정 불가 {unpriced}건 제외
                                </span>
                            )}
                        </div>
                    </div>
                    <div
                        className="rounded-full"
                        style={{ height: 8, background: '#EDF1F7', marginTop: 10, overflow: 'hidden' }}
                    >
                        <div
                            className="rounded-full"
                            style={{
                                width: `${donePct}%`,
                                height: 8,
                                background: '#00A793',
                                transition: 'width .3s',
                            }}
                        />
                    </div>
                </div>
                )}

                {UndeliveredBand}

                {visible.length === 0 && (
                    <div style={{ padding: '40px 24px', textAlign: 'center' }}>
                        <span style={{ fontSize: 15, color: '#93A0AE', display: 'block' }}>
                            진행 중인 지시가 없습니다
                        </span>
                        <span style={{ fontSize: 13, color: '#B4BDC9', display: 'block', marginTop: 6 }}>
                            위 브리핑에서 강사에게 지시를 만드시면 여기에 쌓입니다.
                        </span>
                    </div>
                )}

                {/* 밀린 것 — 먼저 보여야 한다 */}
                {backlog.length > 0 && (
                    <>
                        <GroupHeader
                            title={`밀린 지시 ${backlog.length}건`}
                            hint="어제 이전에 기록했는데 아직 완료되지 않았습니다"
                            color="#C22D38"
                            hintColor="#A7A0A0"
                            bg="#FFFBFB"
                        />
                        {backlog.map((o) => (
                            <OrderRow
                                key={o.orderId}
                                order={o}
                                onCheck={() => markDone(o.orderId)}
                                onRetract={() => retract.ask(o)}
                            />
                        ))}
                    </>
                )}

                {/* 오늘 보낸 것 */}
                {todayTodo.length > 0 && (
                    <>
                        <GroupHeader
                            title={`오늘 기록한 지시 ${todayTodo.length}건`}
                            /*
                             * "강사가 보고하면 자동으로 체크됩니다" 였다가, 알림이 안 나가던
                             * 시절에 "아직 나가지 않습니다 · 직접 전하신 뒤"로 바뀌었다.
                             * 이제 푸시는 나간다. 다만 네모는 여전히 **원장이 직접 처리한 건**을
                             * 표시하는 자리다 — 강사 보고는 강사 앱에서 올라온다.
                             */
                            hint="강사 앱으로 알림이 나갑니다 · 대표님이 직접 처리하신 건만 네모를 눌러 주세요"
                            color="#2C4C8C"
                            hintColor="#93A0AE"
                            bg="#F7F9FC"
                        />
                        {todayTodo.map((o) => (
                            <OrderRow
                                key={o.orderId}
                                order={o}
                                onCheck={() => markDone(o.orderId)}
                                onRetract={() => retract.ask(o)}
                            />
                        ))}
                    </>
                )}

                {/* 끝난 것 */}
                {done.length > 0 && (
                    <>
                        <GroupHeader
                            title={`완료된 일 ${done.length}건`}
                            hint="확인하시면 목록에서 정리됩니다"
                            color="#00795F"
                            hintColor="#8FA79F"
                            bg="#F4FAF8"
                            topBorder
                        />
                        {done.map((o) => (
                            <OrderRow
                                key={o.orderId}
                                order={o}
                                onArchive={() => archive(o.orderId)}
                                onUncheck={() => undoDone(o)}
                                /*
                                 * 완료된 건도 철회할 수 있습니다. 서버가 허용하고,
                                 * 실제로 필요한 경우가 있습니다 — 강사가 완료 보고를
                                 * 한 뒤에야 원장이 "그 회원은 어제 내가 연락했는데"를
                                 * 알아채는 경우입니다. 강사가 한 일(보고·완료 시각)은
                                 * 지워지지 않습니다.
                                 */
                                onRetract={() => retract.ask(o)}
                            />
                        ))}
                    </>
                )}

                {/*
                  ⑤ 철회한 지시 — 접어 둔다.

                  매일 아침 볼 것은 아니지만 **없으면 안 되는** 자리다. 철회하면
                  기본 목록에서 사라지므로, 이 서랍이 없으면 원장은 자기가 뭘
                  취소했는지 확인할 방법이 없다. 눌린 뒤 흔적 없이 사라지는 동작은
                  실수를 바로잡는 장치가 될 수 없다.

                  전달 실패 건은 여기 없다 — 맨 위에 따로 나가 있다.
                */}
                {retractedRest.length > 0 && (
                    <>
                        <button
                            type="button"
                            onClick={() => setShowRetracted((v) => !v)}
                            aria-expanded={showRetracted}
                            className="flex w-full items-center"
                            style={{
                                padding: '11px 22px',
                                background: '#FBFCFE',
                                borderTop: `1px solid ${BORDER}`,
                                borderLeft: 'none',
                                borderRight: 'none',
                                borderBottom: 'none',
                                gap: 8,
                                cursor: 'pointer',
                                textAlign: 'left',
                            }}
                        >
                            {/*
                              "철회한 지시 N건" 이라고만 쓰면 그게 **전부**라는 뜻이 된다.
                              서버 목록은 sentAt 내림차순 200건 창이라 오래된 지시를
                              오늘 철회하면 그 창 밖으로 밀릴 수 있다. 전부라고 말할 수
                              없는 것을 전부라고 말하지 않는다.
                              (상태 필터 요청 — BACKEND_HANDOFF_OPS_ORDER_RETRACT.md)
                            */}
                            <span style={{ fontSize: 14, fontWeight: 800, color: '#5B6B7F' }}>
                                최근 철회한 지시 {retractedRest.length}건
                            </span>
                            {/*
                              아직 보내는 중인 건수는 서랍을 안 펴도 보이게 한다.
                              '전달 대기'는 원장이 할 일은 없지만, 접힌 채로 두면
                              화면이 "다 전달됐다"고 말하는 것과 같아진다.
                            */}
                            {stillSending > 0 && (
                                <span style={{ fontSize: 13, color: '#B45309' }}>
                                    전달 대기 {stillSending}건
                                </span>
                            )}
                            <span style={{ fontSize: 13, color: '#93A0AE' }}>
                                {showRetracted ? '접기' : '펼쳐 보기'}
                            </span>
                        </button>
                        {showRetracted &&
                            retractedRest.map((o) => <RetractedRow key={o.orderId} order={o} />)}
                    </>
                )}

                {/*
                  철회 목록만 못 불러온 경우.

                  진행 현황은 멀쩡하므로 화면 전체를 에러로 덮지 않는다. 다만
                  조용히 비워 두지도 않는다 — 원장이 "철회한 게 없구나"로 읽으면
                  전달 실패한 철회를 영영 못 본다.
                */}
                {retractedQuery.isError && (
                    <div
                        style={{
                            padding: '12px 22px',
                            borderTop: `1px solid ${BORDER}`,
                            background: '#FBFCFE',
                        }}
                    >
                        <span style={{ fontSize: 13, color: '#93A0AE' }}>
                            철회한 지시 목록을 불러오지 못했습니다.{' '}
                        </span>
                        <button
                            type="button"
                            onClick={() => retractedQuery.refetch()}
                            style={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                fontSize: 13,
                                fontWeight: 700,
                                color: '#5B7FC7',
                                cursor: 'pointer',
                            }}
                        >
                            다시 시도
                        </button>
                    </div>
                )}
            </div>

            {retract.modal}

            {toast && (
                <div
                    className="fixed left-1/2 rounded-[10px]"
                    style={{
                        bottom: 40,
                        transform: 'translateX(-50%)',
                        background: '#1A305D',
                        color: '#FFFFFF',
                        fontSize: 15.5,
                        padding: '14px 22px',
                        zIndex: 1000,
                        boxShadow: '0 8px 24px rgba(26,48,93,0.24)',
                    }}
                >
                    {toast}
                </div>
            )}
        </div>
    );
};

/** 금액 태그 — 산정 불가(null)와 0원은 다르다. 0원으로 뭉개지 않는다 */
const ValueChip = ({ order, done }: { order: OrderView; done: boolean }) => {
    if (order.valueAmount == null) {
        return (
            <span
                className="rounded-[6px]"
                style={{
                    background: '#F1F4F9',
                    color: '#7C8794',
                    fontSize: 12.5,
                    fontWeight: 700,
                    padding: '3px 8px',
                }}
                title="단가가 없는 수강권이거나 금액이 걸리지 않는 항목입니다"
            >
                금액 산정 불가
            </span>
        );
    }
    return (
        <span
            className="rounded-[6px]"
            style={{
                background: done ? '#EAF7F2' : order.valueKind === 'GAIN' ? '#F1F5FC' : '#FFF0F0',
                color: done ? '#00795F' : order.valueKind === 'GAIN' ? '#2C4C8C' : '#D92D3A',
                fontSize: 12.5,
                fontWeight: 700,
                padding: '3px 8px',
            }}
        >
            {/*
              * '지켜냄'이 아니라 '완료'다. 완료는 강사가 버튼을 눌렀다는 뜻이고,
              * 그 회원이 실제로 돌아왔는지는 아직 재지 않았다. 금액도 지시 당시의
              * 추정액이라 결과가 아니다.
              */}
            {done ? '완료' : order.valueKind === 'GAIN' ? '확보 가능' : '방어 대상'}{' '}
            {order.valueAmount.toLocaleString('ko-KR')}원
        </span>
    );
};

/** 지시 한 건 */
const OrderRow = ({
    order,
    onArchive,
    onCheck,
    onUncheck,
    onRetract,
}: {
    order: OrderView;
    onArchive?: () => void;
    onCheck?: () => void;
    onUncheck?: () => void;
    /** 잘못 보낸 지시 되돌리기. 확인 창을 연다 — 여기서 바로 나가지 않는다 */
    onRetract?: () => void;
}) => {
    const done = isDone(order);
    const byOwner = order.doneBy === 'OWNER';
    const note = done ? null : pendingNote(order);
    const [hover, setHover] = useState(false);
    /*
     * 보낸 문구는 접어 둔다.
     *
     * 원장님이 "내가 무슨 문구를 보냈지"를 확인할 수 있어야 하지만, 이 목록은
     * 아침에 위에서부터 훑는 자리다. 지시마다 문단이 펼쳐져 있으면 상태(된 것/안 된 것)가
     * 묻힌다. 그래서 기본은 접고, 필요할 때만 편다.
     */
    const [showText, setShowText] = useState(false);
    const ownerNote = order.ownerNote?.trim() ?? '';
    /** 펼쳐 볼 것이 하나라도 있나 — 아무것도 없으면 버튼도 만들지 않는다 */
    const hasText = !!order.memberDraft || !!ownerNote;

    return (
        <div
            className="flex items-start"
            style={{
                padding: '16px 22px',
                borderTop: '1px solid #F1F4F9',
                background: order.status === 'OVERDUE' ? '#FFFBFB' : '#FFFFFF',
                gap: 14,
            }}
        >
            {/* 체크박스 — 이 화면에서 가장 먼저 읽혀야 할 정보 */}
            <button
                type="button"
                onClick={() => {
                    setHover(false);
                    if (done) onUncheck?.();
                    else onCheck?.();
                }}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                className="flex flex-none items-center justify-center"
                title={
                    done
                        ? '눌러서 완료 해제 — 실제로는 실행되지 않았을 때'
                        : '눌러서 완료 처리 — 이미 직접 처리하신 경우에 쓰세요'
                }
                aria-label={done ? `${order.task} 완료 해제` : `${order.task} 완료로 표시`}
                style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    marginTop: 1,
                    padding: 0,
                    // 완료된 항목에 마우스를 올리면 되돌릴 수 있다는 걸 보여준다
                    background: done ? (hover ? '#FFFFFF' : '#00A793') : hover ? '#00A793' : '#FFFFFF',
                    border: done
                        ? `2px solid ${hover ? '#00A793' : '#00A793'}`
                        : hover
                          ? '1px solid #00A793'
                          : `2px solid ${order.status === 'OVERDUE' ? '#F5A9AE' : '#CBD5E3'}`,
                    color: done && hover ? '#00A793' : '#FFFFFF',
                    fontSize: 16,
                    fontWeight: 800,
                    cursor: 'pointer',
                    transition: 'background .15s, border-color .15s, color .15s',
                }}
            >
                {done ? (hover ? '↺' : '✓') : hover ? '✓' : ''}
            </button>

            <div className="flex min-w-0 flex-1 flex-col">
                {/* 무엇을 시켰나 — 할 일 문장이 주인공 */}
                <span
                    style={{
                        fontSize: 16.5,
                        fontWeight: 700,
                        color: done ? '#7C8794' : '#16202E',
                        textDecoration: done ? 'line-through' : 'none',
                        letterSpacing: '-0.01em',
                    }}
                >
                    {order.task}
                </span>

                <div className="flex flex-wrap items-center" style={{ gap: 8, marginTop: 6 }}>
                    <span style={{ fontSize: 14, color: '#5B6B7F' }}>{order.memberName} 회원</span>
                    <span style={{ fontSize: 13.5, color: '#C4CCD8' }}>→</span>
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#5B6B7F' }}>
                        {order.coachName} 강사
                    </span>
                    <ValueChip order={order} done={done} />
                </div>

                {/* 진행 상태 한 줄 */}
                {note && (
                    <span style={{ fontSize: 13.5, color: note.tone, marginTop: 7 }}>{note.text}</span>
                )}

                {/*
                  무슨 문구로 나갔나.

                  두 글은 받는 사람이 다르다 — 회원이 읽을 글(memberDraft)과 강사만 볼
                  말(ownerNote)이다. 한 상자에 몰아넣으면 예전 상태로 되돌아간다.
                  그래서 펼쳤을 때도 칸을 갈라 놓고 누가 읽는지를 라벨에 적는다.
                */}
                {hasText && (
                    <div style={{ marginTop: 8 }}>
                        <button
                            type="button"
                            onClick={() => setShowText((v) => !v)}
                            aria-expanded={showText}
                            style={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                fontSize: 13,
                                fontWeight: 600,
                                color: '#5B7FC7',
                                cursor: 'pointer',
                            }}
                        >
                            {showText ? '보낸 문구 접기' : '보낸 문구 보기'}
                        </button>

                        {showText && (
                            <div style={{ marginTop: 8 }}>
                                {order.memberDraft ? (
                                    <div
                                        className="rounded-[10px]"
                                        style={{
                                            background: '#F7F9FC',
                                            border: '1px solid #E3EAF3',
                                            borderLeft: '3px solid #3D73DD',
                                            padding: '11px 14px',
                                        }}
                                    >
                                        <span
                                            style={{ fontSize: 12.5, fontWeight: 700, color: '#2B529D' }}
                                        >
                                            회원에게 보낼 문구 · {order.memberName} 회원이 읽습니다
                                        </span>
                                        <p
                                            style={{
                                                fontSize: 14,
                                                color: '#2C3A4B',
                                                marginTop: 4,
                                                lineHeight: '21px',
                                                whiteSpace: 'pre-line',
                                            }}
                                        >
                                            “{order.memberDraft}”
                                        </p>
                                    </div>
                                ) : (
                                    /*
                                      문구 없이 나간 지시. "빈칸"으로 두면 화면이 못 읽은 건지
                                      원래 없는 건지 알 수 없다 — 없다는 사실을 말로 적는다.
                                    */
                                    <span
                                        style={{ fontSize: 13, color: '#93A0AE', lineHeight: '19px' }}
                                    >
                                        회원에게 보낼 문구 없이 나갔습니다 · 강사 앱이 첫 마디를 만들어
                                        보여줍니다
                                    </span>
                                )}

                                {ownerNote && (
                                    <div
                                        className="rounded-[10px]"
                                        style={{
                                            marginTop: 8,
                                            background: '#FBFCFE',
                                            border: '1px solid #E3EAF3',
                                            padding: '11px 14px',
                                        }}
                                    >
                                        <span
                                            style={{ fontSize: 12.5, fontWeight: 700, color: '#5B6B7F' }}
                                        >
                                            강사에게 덧붙인 말 · {order.coachName} 강사만 봅니다
                                        </span>
                                        <p
                                            style={{
                                                fontSize: 14,
                                                color: '#2C3A4B',
                                                marginTop: 4,
                                                lineHeight: '21px',
                                                whiteSpace: 'pre-line',
                                            }}
                                        >
                                            “{ownerNote}”
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* 강사 보고 — 다시 열린 건도 보고 내용은 남긴다 */}
                {(done || order.reopened) && order.coachReport && (
                    <div
                        className="rounded-[10px]"
                        style={{
                            marginTop: 9,
                            background: order.reopened ? '#FFFCF3' : byOwner ? '#F7F9FC' : '#F0F7F4',
                            border: `1px solid ${order.reopened ? '#F0D9A8' : byOwner ? '#DCE4EE' : '#C6E5DA'}`,
                            borderLeft: `3px solid ${
                                order.reopened ? '#F0A020' : byOwner ? '#8FA5C4' : '#00A793'
                            }`,
                            padding: '11px 14px',
                        }}
                    >
                        <span
                            style={{
                                fontSize: 12.5,
                                fontWeight: 700,
                                color: order.reopened ? '#B45309' : byOwner ? '#5B6B7F' : '#00795F',
                            }}
                        >
                            {order.reopened
                                ? `${order.coachName} 강사 보고 (다시 열림)`
                                : byOwner
                                  ? '원장님 직접 확인'
                                  : `${order.coachName} 강사 보고`}
                            {order.doneLabel ? ` · ${order.doneLabel}` : ''}
                        </span>
                        <p style={{ fontSize: 14.5, color: '#2C3A4B', marginTop: 4, lineHeight: '21px' }}>
                            “{order.coachReport}”
                        </p>
                    </div>
                )}

                {/*
                  원장이 직접 완료 처리한 건에는 강사 보고가 없다.
                  예전에는 화면이 "강사 보고 없이 직접 완료 처리하셨습니다" 라는 문장을
                  만들어 넣었는데, 그건 강사가 남긴 말이 아니라 화면이 지어낸 말이다.
                */}
                {done && byOwner && !order.coachReport && (
                    <span style={{ fontSize: 13, color: '#93A0AE', marginTop: 7 }}>
                        원장님이 직접 완료 처리하셨습니다 · 강사 보고는 없습니다
                        {order.doneLabel ? ` · ${order.doneLabel}` : ''}
                    </span>
                )}
            </div>

            {/* 액션 */}
            <div className="flex flex-none items-center" style={{ gap: 8, paddingTop: 2 }}>
                {done && onArchive && (
                    <button
                        type="button"
                        onClick={onArchive}
                        className="rounded-[9px]"
                        style={{
                            height: 40,
                            padding: '0 18px',
                            background: '#1A305D',
                            border: 'none',
                            color: '#FFFFFF',
                            fontSize: 14.5,
                            fontWeight: 700,
                            cursor: 'pointer',
                        }}
                    >
                        👍 확인했어요
                    </button>
                )}
                {/*
                  철회 — 잘못 보낸 지시를 되돌린다.

                  ─────────────────────────────────────────────────────────
                  왜 눈에 안 띄게 두는가
                  ─────────────────────────────────────────────────────────
                  이 줄에서 원장이 매일 누르는 것은 체크박스와 '확인했어요'다.
                  철회는 실수를 바로잡을 때만 쓰는 것이고 **되돌릴 수 없다.**
                  그래서 주 버튼과 같은 무게로 그리지 않는다.

                  그렇다고 마우스를 올려야 나타나게 하지도 않는다. 잘못 보냈다는
                  걸 알아챈 원장이 그 순간 찾지 못하면, 지금까지처럼 '확인 처리'로
                  치워 버린다 — 강사 앱에서는 아무 말 없이 사라지는 그 동작이다.
                */}
                {onRetract && (
                    <button
                        type="button"
                        onClick={onRetract}
                        className="rounded-[9px]"
                        aria-label={`${order.task} 지시 철회`}
                        title="잘못 보낸 지시를 되돌립니다 — 강사가 이미 봤다면 알림이 갑니다"
                        style={{
                            height: 40,
                            padding: '0 14px',
                            background: '#FFFFFF',
                            border: '1px solid #E3D2D3',
                            color: '#8C1D24',
                            fontSize: 14,
                            fontWeight: 700,
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        지시 철회
                    </button>
                )}
            </div>
        </div>
    );
};

/**
 * 철회한 지시 한 건.
 *
 * 체크박스가 없습니다 — 철회는 종단이라 되돌릴 수도, 완료할 수도 없습니다.
 * 그 자리에 들어가는 것이 **전달 상태**입니다. 이 화면에서 원장이 알아야 할
 * 것은 "취소했다"가 아니라 **"강사에게 닿았나"** 이기 때문입니다.
 */
const RetractedRow = ({ order }: { order: OrderView }) => {
    const delivery = describeRetractDelivery(order);

    return (
        <div
            className="flex items-start"
            style={{
                padding: '16px 22px',
                borderTop: '1px solid #F1F4F9',
                background: delivery?.needsAction ? '#FFFBFB' : '#FFFFFF',
                gap: 14,
            }}
        >
            <div className="flex min-w-0 flex-1 flex-col">
                <span
                    style={{
                        fontSize: 16.5,
                        fontWeight: 700,
                        color: '#7C8794',
                        textDecoration: 'line-through',
                        letterSpacing: '-0.01em',
                    }}
                >
                    {order.task}
                </span>

                <div className="flex flex-wrap items-center" style={{ gap: 8, marginTop: 6 }}>
                    <span style={{ fontSize: 14, color: '#5B6B7F' }}>{order.memberName} 회원</span>
                    <span style={{ fontSize: 13.5, color: '#C4CCD8' }}>→</span>
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#5B6B7F' }}>
                        {order.coachName} 강사
                    </span>
                    {/*
                      언제 철회했나. sentLabel(보낸 날)이 아니라 retractedLabel 이다 —
                      60일 전 지시를 방금 철회해도 "60일 전"이라고 말하면 안 된다.
                      시각을 못 읽으면 지어내지 않고 그 자리를 비운다.
                    */}
                    {order.retractedLabel && (
                        <span style={{ fontSize: 13.5, color: '#93A0AE' }}>
                            {order.retractedLabel} 철회
                        </span>
                    )}
                </div>

                {/*
                  전달 상태 — 이 줄이 이 행의 주인공이다.

                  네 갈래를 뭉개지 않는다. 특히 '보내는 중'을 '전달됨'으로 쓰지
                  않는다 (retractDelivery.ts 참고).
                */}
                {delivery && (
                    <div
                        className="rounded-[10px]"
                        style={{
                            marginTop: 9,
                            background: delivery.tone.bg,
                            border: `1px solid ${delivery.tone.border}`,
                            borderLeft: `3px solid ${delivery.tone.color}`,
                            padding: '11px 14px',
                        }}
                        // 원장이 움직여야 하는 상태만 읽어 주는 자리로 올린다
                        role={delivery.needsAction ? 'alert' : undefined}
                    >
                        <span style={{ fontSize: 13.5, fontWeight: 700, color: delivery.tone.color }}>
                            {delivery.title}
                        </span>
                        <p
                            style={{
                                fontSize: 13.5,
                                color: '#5B6B7F',
                                marginTop: 3,
                                lineHeight: '20px',
                            }}
                        >
                            {delivery.detail}
                        </p>
                    </div>
                )}

                {/* 왜 철회했나 — 원장이 적은 글. 없으면 없다고 말하지 않고 그냥 비운다 */}
                {order.retractNote && (
                    <div
                        className="rounded-[10px]"
                        style={{
                            marginTop: 8,
                            background: '#FBFCFE',
                            border: `1px solid ${BORDER}`,
                            padding: '11px 14px',
                        }}
                    >
                        {/*
                          누가 읽는가가 갈린다.

                          통보 갈래(알림이 나간 건)에서는 이 글이 강사에게 함께 간다.
                          회수 갈래에서는 **아무도 안 읽는다** — 강사는 그 지시를 본
                          적이 없고 알림도 나가지 않았다. 그런데도 "강사가 읽습니다"라고
                          적으면, 원장은 자기가 쓴 해명이 전달된 줄 안다.
                        */}
                        <span style={{ fontSize: 12.5, fontWeight: 700, color: '#5B6B7F' }}>
                            철회 사유 ·{' '}
                            {delivery && delivery.kind !== 'RECALLED'
                                ? `${order.coachName} 강사에게 함께 갑니다`
                                : '기록으로만 남습니다'}
                        </span>
                        <p
                            style={{
                                fontSize: 14,
                                color: '#2C3A4B',
                                marginTop: 4,
                                lineHeight: '21px',
                                whiteSpace: 'pre-line',
                            }}
                        >
                            “{order.retractNote}”
                        </p>
                    </div>
                )}

                {/*
                  철회 전에 강사가 실제로 한 일은 남긴다.
                  원장의 정정이 강사가 한 일을 지우지 않는다 — 서버도 doneAt·
                  coachReport 를 건드리지 않는다.
                */}
                {order.coachReport && (
                    <span style={{ fontSize: 13, color: '#93A0AE', marginTop: 7 }}>
                        철회 전 {order.coachName} 강사 보고 · “{order.coachReport}”
                    </span>
                )}
            </div>
        </div>
    );
};

export default OrderTracker;
